import React, { useState, useEffect } from 'react';
import { useSearchParams, useParams, Link } from 'react-router-dom';
import { db, auth } from '../lib/firebase';
import { doc, getDoc, addDoc, updateDoc, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { signInWithCustomToken } from 'firebase/auth';
import { PFIFullLogo } from '../components/common/PFILogo';
import { FlamingoLogo } from '../components/common/FlamingoBrand';
import { formatProjectName } from '../utils/projectUtils';
import { formatDate, formatDateLong } from '../utils/dateFormatters';
import { 
  BuildingStorefrontIcon,
  ClockIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  PhoneIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  PaperAirplaneIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ShieldCheckIcon,
  KeyIcon,
  ArrowRightIcon,
  CalendarDaysIcon,
  UserIcon,
  ChatBubbleLeftRightIcon,
  PhotoIcon,
  ClipboardDocumentCheckIcon
} from '@heroicons/react/24/outline';

export default function SupplierQuotePortal() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const taskId = searchParams.get('task'); // Can still be passed as query param if needed
  
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [supplier, setSupplier] = useState(null);
  const [task, setTask] = useState(null);
  const [project, setProject] = useState(null);
  const [quoteRequest, setQuoteRequest] = useState(null);
  const [existingQuote, setExistingQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [fabricImages, setFabricImages] = useState([]);
  const [requestingInfo, setRequestingInfo] = useState(false);
  const [infoRequest, setInfoRequest] = useState('');
  
  const [quoteData, setQuoteData] = useState({
    amount: '',
    description: '',
    timeline: '', // Keep for backward compatibility
    timelineDays: '',
    estimatedCompletionDate: ''
  });
  
  const [savingDraft, setSavingDraft] = useState(false);
  const [isSupplierPortal, setIsSupplierPortal] = useState(false);
  const [supplierQuotes, setSupplierQuotes] = useState([]);
  const [supplierJobSheets, setSupplierJobSheets] = useState([]);

  useEffect(() => {
    if (token) {
      authenticateAndLoadData();
    } else {
      setError('Invalid access link. Please use the link provided in your invitation email.');
      setLoading(false);
    }
  }, [token]);

  const authenticateAndLoadData = async () => {
    try {
      setLoading(true);
      
      // First check for supplier access token (general portal access)
      const supplierAccessSnapshot = await getDocs(
        query(
          collection(db, 'supplierAccess'),
          where('token', '==', token)
        )
      );
      
      if (!supplierAccessSnapshot.empty) {
        // Handle general supplier portal access
        const accessDoc = supplierAccessSnapshot.docs[0];
        const accessData = accessDoc.data();
        
        // Check if token is expired
        if (accessData.expiresAt && accessData.expiresAt.toDate() < new Date()) {
          setError('This access link has expired. Please request a new one from the supplier login page.');
          return;
        }
        
        // Mark as used if not already
        if (!accessData.used) {
          await updateDoc(doc(db, 'supplierAccess', accessDoc.id), {
            used: true,
            usedAt: new Date()
          });
        }
        
        // Load supplier profile view instead of quote form
        await loadSupplierProfile(accessData.supplierId);
        return;
      }
      
      // If not supplier access, check for quote request token
      const quoteRequestsSnapshot = await getDocs(collection(db, 'quoteRequests'));
      let quoteRequest = null;
      
      for (const doc of quoteRequestsSnapshot.docs) {
        const data = doc.data();
        if (data.token === token && !data.archived) {
          quoteRequest = { id: doc.id, ...data };
          break;
        }
      }
      
      if (!quoteRequest) {
        setError('Invalid, expired, or cancelled quote request. Please contact Pink Flamingo Interiors for a new invitation.');
        return;
      }
      
      console.log('Quote request found:', quoteRequest);
      setQuoteRequest(quoteRequest);
      
      // Load project data
      if (!quoteRequest.projectId) {
        console.error('No projectId in quote request');
        setError('Invalid quote request - missing project information. Please contact Pink Flamingo Interiors.');
        return;
      }
      
      const projectDoc = await getDoc(doc(db, 'projects', quoteRequest.projectId));
      if (projectDoc.exists()) {
        const projectData = { id: projectDoc.id, ...projectDoc.data() };
        setProject(projectData);
        console.log('Project loaded:', projectData.name);
        console.log('All project data:', projectData);
      } else {
        console.error('Project not found:', quoteRequest.projectId);
        setProject({
          id: quoteRequest.projectId,
          name: quoteRequest.projectName || 'Unknown Project'
        });
      }
      
      // Load task data
      if (!quoteRequest.taskId) {
        console.error('No taskId in quote request');
        setError('Invalid quote request - missing task information. Please contact Pink Flamingo Interiors.');
        return;
      }
      
      const taskDoc = await getDoc(doc(db, 'projects', quoteRequest.projectId, 'tasks', quoteRequest.taskId));
      if (taskDoc.exists()) {
        setTask({ id: taskDoc.id, ...taskDoc.data() });
        console.log('Task loaded:', taskDoc.data().name);
      } else {
        console.error('Task not found:', quoteRequest.taskId);
        // Use data from quote request as fallback
        setTask({
          id: quoteRequest.taskId,
          name: quoteRequest.taskName || 'Unknown Task',
          description: quoteRequest.taskDescription || '',
          category: quoteRequest.taskCategory || 'general',
          estimatedDays: quoteRequest.estimatedDays || 7,
          budget: quoteRequest.budget || 0
        });
      }
      
      // Set supplier data from quote request
      setSupplier({
        id: quoteRequest.supplierId,
        name: quoteRequest.supplierName,
        email: quoteRequest.supplierEmail,
        phone: quoteRequest.supplierPhone
      });
      
      // Check for existing quote or draft
      const quotesSnapshot = await getDocs(
        query(
          collection(db, 'projects', quoteRequest.projectId, 'tasks', quoteRequest.taskId, 'quotes'),
          where('supplierId', '==', quoteRequest.supplierId)
        )
      );
      
      if (!quotesSnapshot.empty) {
        const existingQuoteData = quotesSnapshot.docs[0].data();
        
        if (existingQuoteData.status === 'submitted') {
          // Show submitted quote
          setExistingQuote({
            id: quotesSnapshot.docs[0].id,
            ...existingQuoteData
          });
        } else if (existingQuoteData.status === 'draft') {
          // Load draft into form
          setQuoteData({
            amount: existingQuoteData.amount ? existingQuoteData.amount.toString() : '',
            description: existingQuoteData.description || '',
            timeline: existingQuoteData.timeline || '',
            notes: existingQuoteData.notes || '',
            materials: existingQuoteData.materials || ''
          });
        }
      }
      
      // Load photos from quote request (includes both general and component-specific photos)
      if (quoteRequest.photos) {
        try {
          // Combine general and component photos for display
          const allPhotos = [
            ...(quoteRequest.photos.general || []),
            ...(quoteRequest.photos.component || [])
          ];
          
          console.log(`Loaded ${allPhotos.length} photos for supplier: ${quoteRequest.photos.general?.length || 0} general + ${quoteRequest.photos.component?.length || 0} component`);
          setPhotos(allPhotos);
        } catch (error) {
          console.error('Error processing photos:', error);
          setPhotos([]);
        }
      } else {
        // Fallback to old method for existing quote requests that don't have photos field
        try {
          const photosQuery = query(
            collection(db, 'projectPhotos'),
            where('projectId', '==', quoteRequest.projectId),
            orderBy('uploadedAt', 'desc')
          );
          
          const photosSnapshot = await getDocs(photosQuery);
          const photosData = photosSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            type: 'general',
            caption: 'General project photo'
          }));
          
          console.log(`Fallback: Loaded ${photosData.length} photos using old method`);
          setPhotos(photosData);
        } catch (error) {
          console.error('Error loading photos:', error);
          setPhotos([]);
        }
      }
      
      // Load fabric images from quote request first, fallback to database
      if (quoteRequest.photos && quoteRequest.photos.fabric && quoteRequest.photos.fabric.length > 0) {
        console.log(`Using fabric images from quote request: ${quoteRequest.photos.fabric.length} images`);
        setFabricImages(quoteRequest.photos.fabric);
      } else if (projectDoc.exists()) {
        // Fallback to loading from database for older quote requests
        try {
          const fabricImagesSnapshot = await getDocs(collection(db, 'projects', quoteRequest.projectId, 'fabricImages'));
          const fabricImagesData = fabricImagesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          console.log(`Fallback: Loaded ${fabricImagesData.length} fabric images from database`);
          setFabricImages(fabricImagesData);
        } catch (error) {
          console.error('Error loading fabric images:', error);
          // Don't fail the whole process if fabric images fail to load
        }
      }
      
      setAuthenticated(true);
      
    } catch (error) {
      console.error('Error authenticating:', error);
      setError('Failed to authenticate. Please check your link or contact Pink Flamingo Interiors.');
    } finally {
      setLoading(false);
    }
  };

  const loadSupplierProfile = async (supplierId) => {
    try {
      // Load supplier data
      const supplierDoc = await getDoc(doc(db, 'suppliers', supplierId));
      if (!supplierDoc.exists()) {
        setError('Supplier profile not found.');
        return;
      }
      
      const supplierData = { id: supplierDoc.id, ...supplierDoc.data() };
      setSupplier(supplierData);
      setIsSupplierPortal(true);
      
      // Load all active quote requests for this supplier
      // Simplified query to avoid index requirements
      const quoteRequestsQuery = query(
        collection(db, 'quoteRequests'),
        where('supplierId', '==', supplierId)
      );
      
      const quoteRequestsSnapshot = await getDocs(quoteRequestsQuery);
      const quotes = [];
      
      for (const qDoc of quoteRequestsSnapshot.docs) {
        const qData = qDoc.data();
        
        // Skip archived quotes
        if (qData.archived === true) {
          continue;
        }
        // Get project and task details for each quote
        let projectData = { name: qData.projectName || 'Unknown Project' };
        let taskData = { name: qData.taskName || 'Unknown Task' };
        
        if (qData.projectId) {
          const projectDoc = await getDoc(doc(db, 'projects', qData.projectId));
          if (projectDoc.exists()) {
            projectData = projectDoc.data();
          }
          
          if (qData.taskId) {
            const taskDoc = await getDoc(doc(db, 'projects', qData.projectId, 'tasks', qData.taskId));
            if (taskDoc.exists()) {
              taskData = taskDoc.data();
            }
          }
        }
        
        quotes.push({
          id: qDoc.id,
          ...qData,
          project: projectData,
          task: taskData
        });
      }
      
      // Sort quotes by createdAt date (newest first)
      quotes.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return dateB - dateA;
      });
      
      setSupplierQuotes(quotes);
      
      // Load acknowledged job sheets for this supplier
      // Simplified query to avoid index requirements
      const jobSheetsQuery = query(
        collection(db, 'jobSheetAcknowledgments'),
        where('supplierId', '==', supplierId)
      );
      
      const jobSheetsSnapshot = await getDocs(jobSheetsQuery);
      const jobSheets = jobSheetsSnapshot.docs.map(jsDoc => ({
        id: jsDoc.id,
        ...jsDoc.data()
      }));
      
      // Sort job sheets by acknowledgedAt date (newest first)
      jobSheets.sort((a, b) => {
        const dateA = a.acknowledgedAt?.toDate ? a.acknowledgedAt.toDate() : new Date(a.acknowledgedAt || 0);
        const dateB = b.acknowledgedAt?.toDate ? b.acknowledgedAt.toDate() : new Date(b.acknowledgedAt || 0);
        return dateB - dateA;
      });
      
      setSupplierJobSheets(jobSheets);
      setAuthenticated(true);
      
    } catch (error) {
      console.error('Error loading supplier profile:', error);
      setError('Failed to load supplier profile. Please try again.');
    }
  };

  const loadDemoData = async () => {
    // Demo supplier data
    setSupplier({
      id: 'supplier-1',
      name: 'ABC Upholstery Services',
      email: 'contact@abcupholstery.com',
      phone: '+61 2 9876 5432',
      category: 'upholstery'
    });

    // Demo task data
    setTask({
      id: taskId || 'task-1',
      name: 'Custom Sofa Reupholstering',
      description: 'Complete reupholstering of a 3-seater sofa with premium fabric. Customer prefers neutral colors that complement their modern living room aesthetic. The sofa frame is in excellent condition and only requires new upholstery and padding replacement.',
      requirements: [
        'Premium grade upholstery fabric',
        'High-density foam padding',
        'Professional finish with hand-stitched details',
        'Color consultation with customer',
        'On-site measurement and delivery'
      ],
      budget: 2500,
      dueDate: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000), // 21 days from now
      status: 'quote_requested',
      createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000) // 2 days ago
    });

    // Demo project data
    setProject({
      id: 'project-1',
      name: 'Modern Living Room Makeover',
      customer: {
        name: 'Sarah Johnson',
        email: 'sarah@email.com',
        phone: '+61 400 123 456'
      },
      address: '123 Heritage Street, Paddington NSW 2021',
      status: 'in_progress'
    });

    // Check for existing quote
    // In real implementation, you'd query Firebase here
    const hasExistingQuote = Math.random() > 0.7; // 30% chance of existing quote for demo
    
    if (hasExistingQuote) {
      setExistingQuote({
        id: 'quote-1',
        amount: 2200,
        description: 'Complete sofa reupholstering with premium Italian leather',
        timeline: '14-16 business days',
        notes: 'Price includes pickup and delivery within 20km of our workshop',
        materials: 'Italian leather, high-grade foam padding, reinforced stitching',
        workmanship: 'premium',
        availability: 'flexible',
        status: 'submitted',
        submittedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)
      });
    }
  };

  const handleSubmitQuote = async (e) => {
    e.preventDefault();
    
    if (!quoteData.amount || !quoteData.description || !quoteData.estimatedCompletionDate) {
      setError('Please fill in all required fields');
      return;
    }

    if (!project?.id || !task?.id || !supplier?.id) {
      setError('Missing project or supplier information. Please refresh and try again.');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      // Check if quote invitation already exists and update it, otherwise create new
      const quotesSnapshot = await getDocs(
        query(
          collection(db, 'projects', project.id, 'tasks', task.id, 'quotes'),
          where('supplierId', '==', supplier.id)
        )
      );

      let quoteId;
      if (!quotesSnapshot.empty) {
        // Update existing quote
        const existingQuoteRef = quotesSnapshot.docs[0].ref;
        await updateDoc(existingQuoteRef, {
          amount: parseFloat(quoteData.amount),
          description: quoteData.description,
          estimatedCompletionDate: quoteData.estimatedCompletionDate,
          status: 'submitted',
          submittedAt: new Date(),
          updatedAt: new Date()
        });
        quoteId = quotesSnapshot.docs[0].id;
      } else {
        // Create new quote
        const newQuoteRef = await addDoc(collection(db, 'projects', project.id, 'tasks', task.id, 'quotes'), {
          supplierId: supplier.id,
          supplierName: supplier.name,
          supplierEmail: supplier.email,
          supplierPhone: supplier.phone || null,
          amount: parseFloat(quoteData.amount),
          description: quoteData.description,
          estimatedCompletionDate: quoteData.estimatedCompletionDate,
          status: 'submitted',
          submittedAt: new Date(),
          magicLinkToken: token
        });
        quoteId = newQuoteRef.id;
      }

      // Update the quote request status
      let quoteRequestDoc = null;
      const quoteRequestsSnapshot = await getDocs(collection(db, 'quoteRequests'));
      for (const doc of quoteRequestsSnapshot.docs) {
        if (doc.data().token === token) {
          await updateDoc(doc.ref, {
            status: 'submitted',
            quoteAmount: parseFloat(quoteData.amount),
            quotedAt: new Date()
          });
          quoteRequestDoc = doc.data();
          break;
        }
      }

      // Update the supplier status in the task's invitedSuppliers array
      if (quoteRequestDoc) {
        const taskRef = doc(db, 'projects', project.id, 'tasks', task.id);
        const taskDoc = await getDoc(taskRef);
        
        if (taskDoc.exists()) {
          const taskData = taskDoc.data();
          const invitedSuppliers = taskData.invitedSuppliers || [];
          
          // Find and update the supplier status
          const updatedSuppliers = invitedSuppliers.map(supplierInfo => {
            if (supplierInfo.id === supplier.id) {
              return {
                ...supplierInfo,
                quoteStatus: 'submitted',
                quoteAmount: parseFloat(quoteData.amount),
                quoteSubmittedAt: new Date()
              };
            }
            return supplierInfo;
          });
          
          // Update the task with the new supplier statuses
          await updateDoc(taskRef, {
            invitedSuppliers: updatedSuppliers,
            updatedAt: new Date()
          });
        }
      }

      // Create notification for PFI team
      await addDoc(collection(db, 'notifications'), {
        type: 'quote-submitted',
        projectId: project.id,
        projectName: project.name,
        taskId: task.id,
        taskName: task.name,
        supplierId: supplier.id,
        supplierName: supplier.name,
        quoteAmount: parseFloat(quoteData.amount),
        message: `${supplier.name} has submitted a quote for ${task.name}`,
        status: 'pending',
        createdAt: new Date()
      });

      // Also create a communication record
      await addDoc(collection(db, 'communications'), {
        projectId: project.id,
        taskId: task.id,
        threadId: null, // This is a new thread (could be linked to original request if we track it)
        messageType: 'pfi-supplier',
        senderId: supplier.id,
        senderType: 'supplier',
        senderName: supplier.name,
        senderEmail: supplier.email || '',
        recipientIds: ['pfi-team'],
        recipientTypes: ['pfi'],
        recipientNames: ['PFI Team'],
        subject: `Quote Submitted: ${task.name}`,
        message: `Quote submitted for "${task.name}" in project "${project.name}".

Amount: $${parseFloat(quoteData.amount).toLocaleString()}
Estimated Completion Date: ${formatDate(quoteData.estimatedCompletionDate)}
Description: ${quoteData.description}`,
        status: 'sent',
        readBy: [supplier.id],
        priority: 'high',
        createdAt: new Date(),
        updatedAt: new Date(),
        relatedTo: {
          type: 'quote-submission',
          referenceId: quoteId
        },
        replyCount: 0
      });

      // Update existing quote state to show success
      setExistingQuote({
        id: quoteId,
        ...quoteData,
        amount: parseFloat(quoteData.amount),
        status: 'submitted',
        submittedAt: new Date()
      });

      // Reset form
      setQuoteData({
        amount: '',
        description: '',
        timeline: '',
        notes: '',
        materials: ''
      });

    } catch (error) {
      console.error('Error submitting quote:', error);
      setError('Failed to submit quote. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestMoreInfo = async () => {
    if (!infoRequest.trim()) {
      setError('Please enter your question or information request');
      return;
    }

    if (!project?.id || !task?.id || !supplier?.id) {
      setError('Missing project or supplier information. Please refresh and try again.');
      return;
    }

    try {
      setRequestingInfo(true);
      setError('');

      // Create notification for PFI team
      await addDoc(collection(db, 'notifications'), {
        type: 'info-request',
        projectId: project.id,
        projectName: project.name,
        taskId: task.id,
        taskName: task.name,
        supplierId: supplier.id,
        supplierName: supplier.name,
        supplierEmail: supplier.email,
        message: `${supplier.name} is requesting more information: ${infoRequest}`,
        requestDetails: infoRequest,
        status: 'pending',
        createdAt: new Date()
      });

      // Also create a communication record  
      await addDoc(collection(db, 'communications'), {
        projectId: project.id,
        taskId: task.id,
        threadId: null, // This could be linked to original quote request thread
        messageType: 'info-request',
        senderId: supplier.id,
        senderType: 'supplier',
        senderName: supplier.name,
        senderEmail: supplier.email || '',
        recipientIds: ['pfi-team'],
        recipientTypes: ['pfi'],
        recipientNames: ['PFI Team'],
        subject: `Information Request: ${task.name}`,
        message: infoRequest,
        status: 'sent',
        readBy: [supplier.id],
        priority: 'high',
        createdAt: new Date(),
        updatedAt: new Date(),
        relatedTo: {
          type: 'info-request',
          referenceId: task.id
        },
        replyCount: 0
      });

      // Show success message
      alert('✅ Your information request has been sent to Pink Flamingo Interiors. They will contact you directly with the requested details.');
      
      // Clear the request
      setInfoRequest('');

    } catch (error) {
      console.error('Error sending information request:', error);
      setError('Failed to send request. Please try again.');
    } finally {
      setRequestingInfo(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!project?.id || !task?.id || !supplier?.id) {
      setError('Missing project or supplier information. Please refresh and try again.');
      return;
    }

    try {
      setSavingDraft(true);
      setError('');

      // Check if draft already exists
      const quotesSnapshot = await getDocs(
        query(
          collection(db, 'projects', project.id, 'tasks', task.id, 'quotes'),
          where('supplierId', '==', supplier.id),
          where('status', '==', 'draft')
        )
      );

      if (!quotesSnapshot.empty) {
        // Update existing draft
        const draftRef = quotesSnapshot.docs[0].ref;
        await updateDoc(draftRef, {
          amount: quoteData.amount ? parseFloat(quoteData.amount) : null,
          description: quoteData.description || '',
          timeline: quoteData.timeline || '',
          timelineDays: quoteData.timelineDays || null,
          notes: quoteData.notes || '',
          materials: quoteData.materials || '',
          updatedAt: new Date()
        });
      } else {
        // Create new draft
        await addDoc(collection(db, 'projects', project.id, 'tasks', task.id, 'quotes'), {
          supplierId: supplier.id,
          supplierName: supplier.name,
          supplierEmail: supplier.email,
          supplierPhone: supplier.phone || null,
          amount: quoteData.amount ? parseFloat(quoteData.amount) : null,
          description: quoteData.description || '',
          timeline: quoteData.timeline || '',
          timelineDays: quoteData.timelineDays || null,
          notes: quoteData.notes || '',
          materials: quoteData.materials || '',
          status: 'draft',
          createdAt: new Date(),
          updatedAt: new Date(),
          magicLinkToken: token
        });
      }

      alert('✅ Draft saved successfully! You can return to complete your quote later using the same link.');

    } catch (error) {
      console.error('Error saving draft:', error);
      setError('Failed to save draft. Please try again.');
    } finally {
      setSavingDraft(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  // Use imported formatDateLong for verbose date display
  const formatDateVerbose = (date) => {
    return formatDateLong(date) || 'Not set';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-25 to-pink-25 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Authenticating and loading quote request...</p>
        </div>
      </div>
    );
  }

  if (error || !authenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-25 to-pink-25 flex items-center justify-center p-4">
        <div className="text-center max-w-lg">
          <PFIFullLogo size="md" className="mx-auto mb-6" />
          <div className="bg-red-50 border border-red-200 rounded-2xl p-8">
            <ExclamationTriangleIcon className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-blue-800 text-sm">
                Please contact Pink Flamingo Interiors at{' '}
                <a href="mailto:hello@pinkflamingointeriors.com.au" className="font-semibold underline">
                  hello@pinkflamingointeriors.com.au
                </a>{' '}
                if you continue to experience issues.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Supplier Profile View (for general portal access)
  if (isSupplierPortal && supplier) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-25 to-pink-25">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-blue-600 via-purple-500 to-pink-400 text-white">
          <div className="absolute inset-0 opacity-10">
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <path d="M0,0 Q50,30 100,0 L100,100 L0,100 Z" fill="white"/>
            </svg>
          </div>
          <div className="relative px-6 py-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <PFIFullLogo size="md" className="text-white" />
                <div className="flex items-center space-x-4">
                  <div className="flex items-center px-4 py-2 bg-white bg-opacity-20 text-gray-900 rounded-lg">
                    <ShieldCheckIcon className="w-4 h-4 mr-2" />
                    Supplier Portal
                  </div>
                </div>
              </div>
              
              <div className="text-center">
                <h1 className="text-4xl font-bold mb-2">Welcome, {supplier.name}</h1>
                <p className="text-white text-opacity-90">Your Pink Flamingo Interiors Supplier Portal</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-6 py-8">
          {/* Supplier Info Card */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <BuildingStorefrontIcon className="w-6 h-6 mr-3 text-blue-600" />
              Your Profile
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600">Business Name</p>
                <p className="font-semibold">{supplier.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-semibold">{supplier.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Phone</p>
                <p className="font-semibold">{supplier.phone}</p>
              </div>
            </div>
          </div>
          
          {/* Active Quote Requests */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <DocumentTextIcon className="w-6 h-6 mr-3 text-green-600" />
              Active Quote Requests ({supplierQuotes.length})
            </h2>
            
            {supplierQuotes.length === 0 ? (
              <p className="text-gray-500">No active quote requests at this time.</p>
            ) : (
              <div className="space-y-4">
                {supplierQuotes.map((quote) => (
                  <div key={quote.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{quote.project.name}</h3>
                        <p className="text-gray-600">{quote.task.name}</p>
                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                          <span className="flex items-center">
                            <CalendarDaysIcon className="w-4 h-4 mr-1" />
                            Requested: {formatDate(quote.createdAt?.toDate ? quote.createdAt.toDate() : quote.createdAt)}
                          </span>
                          {quote.dueDate && (
                            <span className="flex items-center">
                              <ClockIcon className="w-4 h-4 mr-1" />
                              Due: {formatDate(quote.dueDate.toDate ? quote.dueDate.toDate() : quote.dueDate)}
                            </span>
                          )}
                        </div>
                      </div>
                      <a
                        href={`/quote/${quote.token}`}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        View & Respond
                        <ArrowRightIcon className="w-4 h-4 ml-2" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Job Sheets */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <ClipboardDocumentCheckIcon className="w-6 h-6 mr-3 text-purple-600" />
              Acknowledged Job Sheets ({supplierJobSheets.length})
            </h2>
            
            {supplierJobSheets.length === 0 ? (
              <p className="text-gray-500">No job sheets acknowledged yet.</p>
            ) : (
              <div className="space-y-4">
                {supplierJobSheets.map((jobSheet) => (
                  <div key={jobSheet.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold">{jobSheet.projectName}</h3>
                        <p className="text-gray-600">{jobSheet.taskName}</p>
                        <p className="text-sm text-gray-500 mt-1">
                          Acknowledged: {formatDate(jobSheet.acknowledgedAt?.toDate ? jobSheet.acknowledgedAt.toDate() : jobSheet.acknowledgedAt)}
                        </p>
                      </div>
                      {jobSheet.status && (
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          jobSheet.status === 'completed' ? 'bg-green-100 text-green-700' :
                          jobSheet.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {jobSheet.status.replace('_', ' ').charAt(0).toUpperCase() + jobSheet.status.slice(1).replace('_', ' ')}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-25 to-pink-25">
      {/* Header */}
      <div className="relative bg-gradient-to-r from-blue-600 via-purple-500 to-pink-400 text-white">
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <path d="M0,0 Q50,30 100,0 L100,100 L0,100 Z" fill="white"/>
          </svg>
        </div>
        <div className="relative px-6 py-12">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <PFIFullLogo size="md" className="text-white" />
              <div className="flex items-center space-x-4">
                <div className="flex items-center px-4 py-2 bg-white bg-opacity-20 text-gray-900 rounded-lg">
                  <ShieldCheckIcon className="w-4 h-4 mr-2" />
                  Authenticated Access
                </div>
                <Link
                  to={`/supplier-dashboard/${token}`}
                  className="inline-flex items-center px-4 py-2 bg-white bg-opacity-20 text-gray-900 rounded-lg hover:bg-opacity-30 transition-all duration-300"
                >
                  <ClipboardDocumentCheckIcon className="w-4 h-4 mr-2" />
                  My Dashboard
                </Link>
                <a
                  href="mailto:hello@pinkflamingointeriors.com.au"
                  className="inline-flex items-center px-4 py-2 bg-white bg-opacity-20 text-gray-900 rounded-lg hover:bg-opacity-30 transition-all duration-300"
                >
                  <EnvelopeIcon className="w-4 h-4 mr-2" />
                  Contact
                </a>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <h1 className="text-4xl font-bold mb-4">Supplier Quote Portal</h1>
                <p className="text-blue-100 text-xl mb-6">
                  Welcome {supplier?.name}! Please review the quote request and submit your proposal.
                </p>
                
                <div className="flex items-center space-x-6 text-blue-100">
                  <div className="flex items-center">
                    <DocumentTextIcon className="w-5 h-5 mr-2" />
                    Quote Request for {task?.name}
                  </div>
                  <div className="flex items-center">
                    <CalendarDaysIcon className="w-5 h-5 mr-2" />
                    Due: {formatDate(task?.dueDate)}
                  </div>
                </div>
              </div>

              <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-2xl p-6 border border-white border-opacity-30">
                <div className="text-center">
                  <BuildingStorefrontIcon className="w-12 h-12 text-gray-900 mx-auto mb-3" />
                  <div className="text-xl font-bold text-gray-900">{supplier?.name}</div>
                  <div className="text-gray-700">{supplier?.category?.charAt(0).toUpperCase() + supplier?.category?.slice(1)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Quote Request Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Project Information */}
            <div className="bg-white rounded-2xl shadow-xl border border-blue-100 p-8 mb-8">
              <div className="flex items-center mb-6">
                <DocumentTextIcon className="w-7 h-7 text-blue-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">Project Details</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {project?.name && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Project Name</h3>
                    <p className="text-gray-600">{formatProjectName(project)}</p>
                  </div>
                )}
                {(project?.customer?.address || project?.address) && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Project Address</h3>
                    <p className="text-gray-600">{project?.customer?.address || project?.address}</p>
                  </div>
                )}
                {project?.status && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Project Status</h3>
                    <p className="text-gray-600 capitalize">{project.status.replace('_', ' ')}</p>
                  </div>
                )}
                {project?.startDate && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Start Date</h3>
                    <p className="text-gray-600">{formatDate(project.startDate)}</p>
                  </div>
                )}
                {project?.expectedEndDate && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Expected End Date</h3>
                    <p className="text-gray-600">{formatDate(project.expectedEndDate)}</p>
                  </div>
                )}
                {project?.budget && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Total Project Budget</h3>
                    <p className="text-gray-600">{formatCurrency(project.budget)} (indicative)</p>
                  </div>
                )}
                {project?.designStyle && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Design Style</h3>
                    <p className="text-gray-600 capitalize">{project.designStyle}</p>
                  </div>
                )}
                {project?.rooms && project?.rooms.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Rooms Involved</h3>
                    <p className="text-gray-600">{project.rooms.join(', ')}</p>
                  </div>
                )}
                {project?.projectType && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Project Type</h3>
                    <p className="text-gray-600 capitalize">{project.projectType}</p>
                  </div>
                )}
              </div>

              {(project?.description || project?.projectDescription) && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Project Description</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {project.projectDescription || project.description}
                    </p>
                  </div>
                </div>
              )}

              {project?.specialRequirements && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Special Requirements</h3>
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{project.specialRequirements}</p>
                  </div>
                </div>
              )}

              {project?.notes && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Project Notes</h3>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{project.notes}</p>
                  </div>
                </div>
              )}
            </div>


            {/* Task Information */}
            <div className="bg-white rounded-2xl shadow-xl border border-green-100 p-8">
              <div className="flex items-center mb-6">
                <ClipboardDocumentCheckIcon className="w-7 h-7 text-green-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">Task Requirements</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {task?.name && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Task Name</h3>
                    <p className="text-gray-600">{task.name}</p>
                  </div>
                )}
                {task?.category && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Category</h3>
                    <p className="text-gray-600 capitalize">{task.category}</p>
                  </div>
                )}
                {task?.estimatedDays && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Estimated Days</h3>
                    <p className="text-gray-600">{task.estimatedDays} days</p>
                  </div>
                )}
                {task?.priority && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Priority</h3>
                    <p className="text-gray-600 capitalize">{task.priority}</p>
                  </div>
                )}
                {task?.dueDate && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Due Date</h3>
                    <p className="text-gray-600">{formatDate(task.dueDate)}</p>
                  </div>
                )}
                {task?.status && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Task Status</h3>
                    <p className="text-gray-600 capitalize">{task.status.replace('_', ' ')}</p>
                  </div>
                )}
                {task?.materials && task?.materials.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Required Materials</h3>
                    <p className="text-gray-600">{task.materials.join(', ')}</p>
                  </div>
                )}
              </div>

              {task?.description && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Task Description</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{task.description}</p>
                  </div>
                </div>
              )}

              {task?.requirements && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Specific Requirements</h3>
                  <div className="bg-green-50 rounded-lg p-4">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{task.requirements}</p>
                  </div>
                </div>
              )}

              {task?.notes && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Additional Notes</h3>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{task.notes}</p>
                  </div>
                </div>
              )}

              {task?.requirements && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Requirements</h3>
                  <ul className="space-y-2">
                    {task.requirements.map((req, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Product Specifications */}
            {quoteRequest?.productSpecs && (
              <div className="bg-white rounded-2xl shadow-xl border border-indigo-100 p-8">
                <div className="flex items-center mb-6">
                  <DocumentTextIcon className="w-7 h-7 text-indigo-600 mr-3" />
                  <h2 className="text-2xl font-bold text-gray-900">Product Specifications</h2>
                </div>
                
                <div className="space-y-6">
                  {/* Product Details */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {quoteRequest.productSpecs.product && (
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">Product</h3>
                        <p className="text-gray-600">{quoteRequest.productSpecs.product}</p>
                      </div>
                    )}
                    {quoteRequest.productSpecs.category && (
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">Category</h3>
                        <p className="text-gray-600">{quoteRequest.productSpecs.category}</p>
                      </div>
                    )}
                    {quoteRequest.productSpecs.subcategory && (
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">Type</h3>
                        <p className="text-gray-600">{quoteRequest.productSpecs.subcategory}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Features */}
                  {quoteRequest.productSpecs.features && quoteRequest.productSpecs.features.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3">Features</h3>
                      <div className="flex flex-wrap gap-2">
                        {quoteRequest.productSpecs.features.map((feature, index) => (
                          <span 
                            key={index} 
                            className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Dimensions */}
                  {quoteRequest.productSpecs.dimensions && Object.keys(quoteRequest.productSpecs.dimensions).length > 0 && (
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3">Dimensions</h3>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {Object.entries(quoteRequest.productSpecs.dimensions).map(([key, value]) => (
                            <div key={key} className="flex justify-between items-center p-2 bg-white rounded border">
                              <span className="text-sm text-gray-600 capitalize font-medium">
                                {key.replace(/([A-Z])/g, ' $1').trim()}:
                              </span>
                              <span className="text-gray-800 font-semibold">{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Fabric Information */}
                  {quoteRequest.productSpecs.fabric && (
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3">Fabric Requirements</h3>
                      <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                        <p className="text-gray-700 mb-4">{quoteRequest.productSpecs.fabric.description}</p>
                        
                        {/* Show fabric images if available */}
                        {fabricImages.length > 0 && (
                          <div>
                            <p className="text-sm text-amber-700 font-medium mb-3">
                              📎 Fabric Images ({fabricImages.length} image{fabricImages.length !== 1 ? 's' : ''})
                            </p>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                              {fabricImages.map((image) => (
                                <div 
                                  key={image.id} 
                                  className="relative group cursor-pointer" 
                                  onClick={() => window.open(image.url, '_blank')}
                                >
                                  <img
                                    src={image.url}
                                    alt={image.description || 'Fabric image'}
                                    className="w-full h-24 object-cover rounded-md shadow-md hover:shadow-lg transition-shadow border border-yellow-300"
                                  />
                                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 rounded-md flex items-center justify-center">
                                    <div className="text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity bg-black bg-opacity-50 px-2 py-1 rounded">
                                      Click to enlarge
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Show count if no images loaded but count exists */}
                        {fabricImages.length === 0 && quoteRequest.productSpecs.fabric.imageCount > 0 && (
                          <p className="text-sm text-amber-700 font-medium">
                            📎 {quoteRequest.productSpecs.fabric.imageCount} fabric image{quoteRequest.productSpecs.fabric.imageCount !== 1 ? 's' : ''} provided
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Project Description */}
                  {quoteRequest.projectDescription && (
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3">Additional Context</h3>
                      <div className="bg-blue-50 rounded-lg p-4">
                        <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                          {quoteRequest.projectDescription}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Project Photos */}
            {photos.length > 0 && (
              <div className="bg-white rounded-2xl shadow-xl border border-purple-100 p-8">
                <div className="flex items-center mb-6">
                  <PhotoIcon className="w-7 h-7 text-purple-600 mr-3" />
                  <h2 className="text-2xl font-bold text-gray-900">Project Photos</h2>
                  <span className="ml-auto text-sm text-gray-500">
                    {photos.length} photo{photos.length !== 1 ? 's' : ''}
                  </span>
                </div>
                
                <div className="space-y-8">
                  {/* General Project Photos */}
                  {(() => {
                    const generalPhotos = photos.filter(p => p.type === 'general');
                    return generalPhotos.length > 0 ? (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                          <span className="bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full mr-3">General</span>
                          Project Context Photos ({generalPhotos.length})
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                          {generalPhotos.map((photo) => (
                            <div key={photo.id} className="relative group cursor-pointer" onClick={() => window.open(photo.url, '_blank')}>
                              <img
                                src={photo.url}
                                alt="General project photo"
                                className="w-full h-32 object-cover rounded-lg shadow-md hover:shadow-lg transition-shadow"
                              />
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 rounded-lg flex items-center justify-center">
                                <div className="text-white text-sm opacity-0 group-hover:opacity-100 transition-opacity bg-black bg-opacity-50 px-2 py-1 rounded">
                                  Click to enlarge
                                </div>
                              </div>
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent rounded-b-lg p-2">
                                <p className="text-white text-xs">General project context</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null;
                  })()}

                  {/* Component-Specific Photos */}
                  {(() => {
                    const componentPhotos = photos.filter(p => p.type === 'component');
                    return componentPhotos.length > 0 ? (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                          <span className="bg-pink-100 text-pink-800 text-sm px-3 py-1 rounded-full mr-3">Component</span>
                          Specific to Your Quote ({componentPhotos.length})
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                          {componentPhotos.map((photo) => (
                            <div key={photo.id} className="relative group cursor-pointer" onClick={() => window.open(photo.url, '_blank')}>
                              <img
                                src={photo.url}
                                alt={`${photo.componentName || 'Component'} photo`}
                                className="w-full h-32 object-cover rounded-lg shadow-md hover:shadow-lg transition-shadow border-2 border-pink-200"
                              />
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 rounded-lg flex items-center justify-center">
                                <div className="text-white text-sm opacity-0 group-hover:opacity-100 transition-opacity bg-black bg-opacity-50 px-2 py-1 rounded">
                                  Click to enlarge
                                </div>
                              </div>
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent rounded-b-lg p-2">
                                <p className="text-white text-xs font-medium">
                                  {photo.componentName || 'Component specific'}
                                </p>
                              </div>
                              <div className="absolute top-2 right-2 bg-pink-600 text-white text-xs px-2 py-1 rounded-full font-medium">
                                Your Item
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
                
                <div className="mt-6 p-4 bg-purple-50 rounded-lg">
                  <p className="text-sm text-purple-800">
                    💡 <strong>Photo Guide:</strong>
                  </p>
                  <ul className="text-sm text-purple-700 mt-2 ml-4 space-y-1">
                    <li>• <strong>General photos</strong> show the overall project context and room</li>
                    <li>• <strong>Component photos</strong> show the specific items you're quoting for</li>
                    <li>• Click any photo to view full size and examine details</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Existing Quote or Quote Form */}
            {existingQuote ? (
              <div className="bg-white rounded-2xl shadow-xl border border-green-100 p-8">
                <div className="flex items-center mb-6">
                  <CheckCircleIcon className="w-7 h-7 text-green-600 mr-3" />
                  <h2 className="text-2xl font-bold text-gray-900">Your Submitted Quote</h2>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-2xl font-bold text-green-800">
                        {formatCurrency(existingQuote.amount)}
                      </div>
                      <div className="text-green-600">Quote Amount</div>
                    </div>
                    <div className="text-right">
                      <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                        Submitted
                      </div>
                      <div className="text-sm text-green-600 mt-1">
                        {formatDate(existingQuote.submittedAt)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <div>
                      <div className="text-sm font-medium text-green-700">Estimated Completion Date</div>
                      <div className="text-green-800">{formatDate(existingQuote.estimatedCompletionDate || existingQuote.timeline)}</div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <div className="text-sm font-medium text-green-700">Description</div>
                      <div className="text-green-800">{existingQuote.description}</div>
                    </div>
                    
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <InformationCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <div className="text-blue-800 text-sm">
                      <p className="font-semibold mb-1">Quote Successfully Submitted!</p>
                      <p>Pink Flamingo Interiors will review your quote and contact you directly. You can update your quote by contacting us at the email address below.</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Request More Information Section */}
                <div className="bg-white rounded-2xl shadow-xl border border-blue-100 p-8 mb-8">
                  <div className="flex items-center mb-6">
                    <ChatBubbleLeftRightIcon className="w-7 h-7 text-blue-600 mr-3" />
                    <h2 className="text-2xl font-bold text-gray-900">Need More Information?</h2>
                  </div>
                  
                  <p className="text-gray-600 mb-6">
                    If you need additional details, clarifications, or specifications before submitting your quote, 
                    use the form below to request more information from Pink Flamingo Interiors.
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        What additional information do you need?
                      </label>
                      <textarea
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Please describe what additional information, clarifications, or specifications you need to provide an accurate quote..."
                        value={infoRequest}
                        onChange={(e) => setInfoRequest(e.target.value)}
                      />
                    </div>
                    
                    <button
                      onClick={handleRequestMoreInfo}
                      disabled={requestingInfo || !infoRequest.trim()}
                      className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {requestingInfo ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline-block mr-2"></div>
                          Sending Request...
                        </>
                      ) : (
                        <>
                          <ChatBubbleLeftRightIcon className="w-4 h-4 mr-2 inline-block" />
                          Request Information
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Submit Quote Section */}
                <div className="bg-white rounded-2xl shadow-xl border border-purple-100 p-8">
                  <div className="flex items-center mb-6">
                    <PaperAirplaneIcon className="w-7 h-7 text-purple-600 mr-3" />
                    <h2 className="text-2xl font-bold text-gray-900">Submit Your Quote</h2>
                  </div>

                <form onSubmit={handleSubmitQuote} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Quote Amount (AUD) *
                      </label>
                      <div className="relative">
                        <CurrencyDollarIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="number"
                          required
                          min="0"
                          step="0.01"
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="2500.00"
                          value={quoteData.amount}
                          onChange={(e) => setQuoteData({ ...quoteData, amount: e.target.value })}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Estimated Completion Date *
                      </label>
                      <input
                        type="date"
                        required
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        value={quoteData.estimatedCompletionDate || ''}
                        onChange={(e) => setQuoteData({ ...quoteData, estimatedCompletionDate: e.target.value })}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Expected date to complete the work
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Service Description *
                    </label>
                    <textarea
                      rows={4}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Describe your proposed solution, approach, and what's included in your quote..."
                      value={quoteData.description}
                      onChange={(e) => setQuoteData({ ...quoteData, description: e.target.value })}
                    />
                  </div>


                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <ExclamationTriangleIcon className="w-5 h-5 text-red-600 mr-2" />
                        <span className="text-red-800">{error}</span>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-6">
                    <div className="flex items-center space-x-4">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="px-8 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {submitting ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline-block mr-2"></div>
                            Submitting...
                          </>
                        ) : (
                          <>
                            <PaperAirplaneIcon className="w-4 h-4 mr-2 inline-block" />
                            Submit Quote
                          </>
                        )}
                      </button>
                      
                      <button
                        type="button"
                        onClick={handleSaveDraft}
                        disabled={savingDraft}
                        className="px-6 py-3 bg-gray-500 text-white font-semibold rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {savingDraft ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline-block mr-2"></div>
                            Saving...
                          </>
                        ) : (
                          'Save Draft'
                        )}
                      </button>
                    </div>
                    
                    <div className="text-sm text-gray-500">
                      * Required fields for submission
                    </div>
                  </div>
                </form>
                </div>
              </>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Project Contact */}
            <div className="bg-white rounded-2xl shadow-xl border border-blue-100 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Project Contact</h3>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <UserIcon className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <div className="font-medium text-gray-900">Pink Flamingo Interiors</div>
                    <div className="text-sm text-gray-500">Project Contact</div>
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <div className="text-sm text-gray-600 mb-2">Project Address:</div>
                  <div className="text-gray-900">{project?.address}</div>
                </div>
              </div>
            </div>

            {/* Support */}
            <div className="bg-white rounded-2xl shadow-xl border border-pink-100 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Questions About This Project?</h3>
              <p className="text-sm text-gray-600 mb-4">Need clarification on project requirements, timeline, or specifications? Contact us directly:</p>
              
              <div className="space-y-3">
                <a
                  href="tel:+61414434999"
                  className="w-full flex items-center justify-center px-4 py-3 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                >
                  <PhoneIcon className="w-4 h-4 mr-2" />
                  Call Us: (04) 1443 4999
                </a>
                <a
                  href="mailto:quote@pinkflamingointeriors.com.au"
                  className="w-full flex items-center justify-center px-4 py-3 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <EnvelopeIcon className="w-4 h-4 mr-2" />
                  Email Support
                </a>
              </div>

              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <div className="text-xs text-gray-600">
                  <p className="font-medium mb-1">Business Hours:</p>
                  <p>Mon-Fri: 9:00 AM - 6:00 PM</p>
                  <p>Sat: 10:00 AM - 4:00 PM</p>
                </div>
              </div>
            </div>

            {/* Quote Guidelines */}
            <div className="bg-white rounded-2xl shadow-xl border border-orange-100 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Quote Guidelines</h3>
              
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Include all materials and labor
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Specify delivery timeline
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Mention warranty terms
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Note any additional fees
                </li>
              </ul>
            </div>

            {/* Security Notice */}
            <div className="bg-white rounded-2xl shadow-xl border border-green-100 p-6">
              <div className="flex items-center mb-3">
                <ShieldCheckIcon className="w-5 h-5 text-green-600 mr-2" />
                <h3 className="text-lg font-bold text-gray-900">Secure Access</h3>
              </div>
              
              <p className="text-sm text-gray-600 mb-3">
                This portal uses secure authentication to protect sensitive project information.
              </p>
              
              <div className="flex items-center text-xs text-green-600">
                <KeyIcon className="w-3 h-3 mr-1" />
                Magic link authenticated
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center space-x-2 text-gray-500">
            <FlamingoLogo size="sm" className="text-pink-500" />
            <span className="text-sm">"Fun and Fabulous without the Fuss" ✨</span>
          </div>
        </div>
      </div>
    </div>
  );
}
